import { useMemo } from 'react';

export function useChartableColumns(rows, columns) {
  return useMemo(() => {
    const NON_CHARTABLE_NAME =
      /(_id$|^id$|uuid|timestamp|createdAt|updatedAt|created_at|updated_at|date)/i;
    const OBJECTID_RE = /^[0-9a-f]{24}$/i;
    const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;
    const sampleRows = rows.slice(0, 50);

    return columns.filter((col) => {
      if (!col?.field) return false;
      if (NON_CHARTABLE_NAME.test(col.field)) return false;
      if (col.field.toLowerCase().endsWith('_id')) return false;

      if (!sampleRows.length) return true;

      const values = [];
      for (const r of sampleRows) {
        const v = r[col.field];
        if (v !== null && v !== undefined && v !== '') values.push(String(v));
      }

      if (!values.length) return false;

      const objectIdCount = values.filter((v) => OBJECTID_RE.test(v)).length;
      if (objectIdCount > values.length * 0.3) return false;

      const dateCount = values.filter((v) => ISO_DATE_RE.test(v)).length;
      if (dateCount > values.length * 0.3) return false;

      const avgLen =
        values.reduce((sum, v) => sum + v.length, 0) / values.length;
      if (avgLen > 60) return false;

      const distinct = new Set(values).size;
      const allNumeric = values.every(
        (v) => v.trim() !== '' && !isNaN(Number(v))
      );
      if (allNumeric && distinct > 15) return false;

      return distinct >= 2 && distinct <= 30;
    });
  }, [rows, columns]);
}

export const ROW_CONTEXT_KEYS = {
  familyId: '__ctx_familyId',
  familyMemberId: '__ctx_familyMemberId',
  noteType: '__ctx_noteType'
};

export const DEFAULT_VIEW_KEY = 'patient_overview';

export const ALL_EVENTS_VIEW_KEY = 'all_events';

export const RELATIONSHIP_MAX_DEPTH = 2;
export const BUILD_FIRST_RELATIONSHIP_MAX_DEPTH = 3;

export const VIEW_QUERY_PARAM = 'view';

export const ALL_TILES = [
  { key: 'total', label: 'Total Records' },
  { key: 'mutationCarriers', label: 'Mutation Carriers' },
  { key: 'sample', label: 'Sample' },
  { key: 'treatmentsRecorded', label: 'Treatments Recorded' },
  { key: 'activePregnancies', label: 'Active Pregnancies' },
  { key: 'genotypes', label: 'Genotypes' },
  { key: 'tests', label: 'Tests' },
  { key: 'diagnoses', label: 'Diagnoses' },
  { key: 'phenotypes', label: 'Phenotypes' }
];

export const TILE_META = [
  {
    key: 'total',
    title: 'TOTAL RECORDS',
    color: '#2ba9bf',
    subtext: 'Total number of records',
    trendData: [{ v: 10 }, { v: 15 }, { v: 8 }, { v: 12 }, { v: 20 }]
  },
  {
    key: 'mutationCarriers',
    title: 'MUTATION CARRIERS',
    color: '#dc2626',
    subtext: 'Confirmed mutation carriers',
    trendData: [{ v: 2 }, { v: 4 }, { v: 3 }, { v: 6 }, { v: 5 }]
  },
  {
    key: 'sample',
    title: 'SAMPLE',
    color: '#0891b2',
    subtext: 'Samples in progress',
    trendData: [{ v: 6 }, { v: 9 }, { v: 7 }, { v: 11 }, { v: 8 }]
  },
  {
    key: 'treatmentsRecorded',
    title: 'TREATMENTS RECORDED',
    color: '#16a34a',
    subtext: 'Treatment records on file',
    trendData: [{ v: 4 }, { v: 6 }, { v: 5 }, { v: 8 }, { v: 7 }]
  },
  {
    key: 'activePregnancies',
    title: 'PREGNANCIES',
    color: '#db2777',
    subtext: 'Registered pregnancies',
    trendData: [{ v: 3 }, { v: 5 }, { v: 4 }, { v: 6 }, { v: 5 }]
  },
  {
    key: 'genotypes',
    title: 'GENOTYPES',
    color: '#b45309',
    subtext: 'Genetic results recorded',
    trendData: [{ v: 3 }, { v: 5 }, { v: 4 }, { v: 7 }, { v: 6 }]
  },
  {
    key: 'tests',
    title: 'TESTS',
    color: '#0369a1',
    subtext: 'Tests performed',
    trendData: [{ v: 5 }, { v: 8 }, { v: 6 }, { v: 10 }, { v: 9 }]
  },
  {
    key: 'diagnoses',
    title: 'DIAGNOSES',
    color: '#be185d',
    subtext: 'Clinical diagnoses recorded',
    trendData: [{ v: 4 }, { v: 6 }, { v: 5 }, { v: 8 }, { v: 7 }]
  },
  {
    key: 'phenotypes',
    title: 'PHENOTYPES',
    color: '#7c3aed',
    subtext: 'Phenotypic observations recorded',
    trendData: [{ v: 3 }, { v: 5 }, { v: 4 }, { v: 7 }, { v: 6 }]
  }
];

const BASE_TILE_QUERY = {
  joins: [],
  preFilter: null,
  changedPreFilter: null,
  filterModel: { items: [], logicOperator: 'and' },
  sortModel: []
};

export const TILE_QUERIES = {
  total: {
    ...BASE_TILE_QUERY,
    primaryModel: 'User'
  },

  mutationCarriers: {
    ...BASE_TILE_QUERY,
    primaryModel: 'RiskFactor',
    joins: ['User'],
    preFilter: { isMutation: true }
  },

  sample: {
    ...BASE_TILE_QUERY,
    primaryModel: 'Sample'
  },

  treatmentsRecorded: {
    ...BASE_TILE_QUERY,
    primaryModel: 'Treatment'
  },

  activePregnancies: {
    ...BASE_TILE_QUERY,
    primaryModel: 'Pregnancy',
    joins: ['User']
  },

  genotypes: {
    ...BASE_TILE_QUERY,
    primaryModel: 'User',
    changedPreFilter: { 'genotypes.0': { $exists: true } },
    filterModel: {
      items: [{ field: 'User_genotypes', operator: 'isNotEmpty', id: 1 }],
      logicOperator: 'and'
    }
  },

  tests: {
    ...BASE_TILE_QUERY,
    primaryModel: 'Test'
  },

  diagnoses: {
    ...BASE_TILE_QUERY,
    primaryModel: 'User',
    changedPreFilter: { 'diagnoses.0': { $exists: true } },
    filterModel: {
      items: [{ field: 'User_diagnoses', operator: 'isNotEmpty', id: 1 }],
      logicOperator: 'and'
    }
  },

  phenotypes: {
    ...BASE_TILE_QUERY,
    primaryModel: 'User',
    changedPreFilter: { 'phenotypes.0': { $exists: true } },
    filterModel: {
      items: [{ field: 'User_phenotypes', operator: 'isNotEmpty', id: 1 }],
      logicOperator: 'and'
    }
  }
};

export const CHART_COLORS = [
  '#2ba9bf',
  '#4f46e5',
  '#f59e0b',
  '#06b6d4',
  '#ec4899',
  '#8b5cf6',
  '#10b981',
  '#f43f5e'
];
